// WeekView.jsx

import React, { useLayoutEffect, useMemo, useRef } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';
import { groupAllDayEventsByDateKey, groupTimedEventsByDateHour } from 'views/calendar/shared/calendarUtils';

// Hooks
import useCalendarDateFields from 'views/calendar/hooks/useCalendarDateFields';
import useCalendarEvents from 'views/calendar/hooks/useCalendarEvents';

// CSS
import './WeekView.css';

// Components
import { addDays, startOfWeek } from 'date-fns';
import CalendarNavControls from 'views/calendar/shared/CalendarNavControls';
import CalendarRangePanel from 'views/calendar/shared/CalendarRangePanel';

/**
 * WeekView
 * 
 * Renders a week view for a calendar, showing events for the selected date's week.
 * 
 * @param {Object} view - View configuration.
 * @param {Object} typeDef - Type definition for events.
 * @param {Array} nodes - Event nodes to display.
 * @param {string} language - Language code for localization (default: 'en').
 *  
 *
 * @returns {ReactNode} - WeekView.
 */
const WeekView = React.memo(({
    view,
    typeDef,
    nodes = [],
    eventItems,
    language = 'en',
    onNodeClick,
    onNodeDoubleClick,
    onAddDate,
    onAddDateTime,
    canDragEvents = false,
    draggingItem = null,
    dragOverTargetKey = null,
    onEventDragStart,
    onEventDragEnd,
    onDropTargetDragOver,
    onMoveToTarget,
}) => {

    // ----- CONTEXT ----- //

    // Global Context
    const {
        selectedDate,
        setSelectedDate,
    } = useCalendarTimeline();

    // ----- REFS ----- //

    const containerRef = useRef(null);

    // ----- HOOKS ----- //

    const { typeKey, dateFields } = useCalendarDateFields(typeDef, { preferredFieldKey: view?.calendarField });

    // ----- MEMOS ----- //

    // Base date for the month view (normalized selected date or current date)

    const baseDate = useMemo(
        () => normalizeDateValue(selectedDate) || new Date(),
        [selectedDate]
    );

    // Calculate the start of the week based on the base date
    const weekStart = useMemo(
        () => startOfWeek(baseDate, { weekStartsOn: 0 }),
        [baseDate]
    );

    // Generate an array of dates for the week (7 days starting from weekStart)
    const weekDays = useMemo(() => (
        Array.from({ length: 7 }, (_, index) => addDays(weekStart, index))
    ), [weekStart]);

    // Today's date (used for highlighting current day)
    const today = useMemo(() => new Date(), []);

    // Generate a label for the current week range (e.g., "March 3, 2024 - March 9, 2024")
    const weekRangeLabel = useMemo(() => {
        const weekEnd = addDays(weekStart, 6);
        const format = (date) => date.toLocaleDateString(language || 'en', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
        return `${format(weekStart)} - ${format(weekEnd)}`;
    }, [weekStart, language]);

    // ----- HOOKS ----- //

    // Fetch and process events for the week view

    const resolvedEventItems = useCalendarEvents({
        nodes,
        dateFields,
        typeKey,
        eventItems,
        rangeStart: weekStart,
        rangeEnd: addDays(weekStart, 6),
    });

    // ----- MEMOS ----- //

    // Group events by date key for the week view

    const allDayEventsByDate = useMemo(() => {
        const weekEnd = addDays(weekStart, 6);

        return groupAllDayEventsByDateKey(
            resolvedEventItems,
            (item) => item.day >= weekStart && item.day <= weekEnd
        );
    }, [resolvedEventItems, weekStart]);

    const timedEventsByDateHour = useMemo(() => {
        const weekEnd = addDays(weekStart, 6);

        return groupTimedEventsByDateHour(
            resolvedEventItems,
            (item) => item.day >= weekStart && item.day <= weekEnd
        );
    }, [resolvedEventItems, weekStart]);

    // ----- HANDLERS ----- //

    /**
     * Handles week navigation.
     * 
     * @param {number} direction - Direction to navigate (-1 for previous, 1 for next).
     */

    const handleWeekChange = (direction) => {
        const nextDate = addDays(baseDate, direction * 7);
        setSelectedDate(nextDate);
    };

    /**
     * Handles navigating to today's date when the "Today" button is clicked.
     */
    const handleGoToToday = () => {
        setSelectedDate(new Date());
    };

    // ----- EFFECTS ----- //

    useLayoutEffect(() => {
        const container = containerRef.current;
        const grid = container?.querySelector('.calendar-range-panel-grid');
        const scrollParent = container?.parentElement;
        if (!container || !grid || !scrollParent) return undefined;

        const syncHourRowMinHeight = () => {
            const availableHeight = scrollParent.clientHeight - grid.offsetTop;
            const hourRowMinHeight = Math.max(40, Math.floor(availableHeight / 24));
            container.style.setProperty('--calendar-range-hour-row-min', `${hourRowMinHeight}px`);
        };

        syncHourRowMinHeight();

        const resizeObserver = new ResizeObserver(syncHourRowMinHeight);
        resizeObserver.observe(scrollParent);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    // ----- RENDER ----- //

    return (

        <div
          className="week-view-container"
          style={{
            '--calendar-range-columns': 7,
          }}
          ref={containerRef}
        >
            <CalendarNavControls
              title={weekRangeLabel}
              prevAriaLabel="Previous week"
              nextAriaLabel="Next week"
              onNext={() => handleWeekChange(1)}
              onPrev={() => handleWeekChange(-1)}
              onToday={handleGoToToday}
            />
            <CalendarRangePanel
              headerLabel="Week"
              days={weekDays}
              selectedDate={baseDate}
              today={today}
              language={language}
              eventsByDate={allDayEventsByDate}
              allDayEventsByDate={allDayEventsByDate}
              timedEventsByDateHour={timedEventsByDateHour}
              canDragEvents={canDragEvents}
              draggingItem={draggingItem}
              dragOverTargetKey={dragOverTargetKey}
              onAddDate={onAddDate}
              onAddDateTime={onAddDateTime}
              onDropTargetDragOver={onDropTargetDragOver}
              onEventDragEnd={onEventDragEnd}
              onEventDragStart={onEventDragStart}
              onMoveToTarget={onMoveToTarget}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              onSelectDate={setSelectedDate}
            />
        </div>
    );

});

// Dev Tools
WeekView.displayName = 'WeekView';

export default WeekView;
